// S40 AI 金鑰登錄台(AI key registry)。
//
// 平台擁有者集中管理各 consumer(LLM API 金鑰消費端)的金鑰。目前兩個 consumer:
//   admin            — admin 管理助理(右下角浮窗)用
//   developer-portal — dev-portal-assistant(開發助手)/ dev-portal-diagnostics(診斷),已接線
//
// 安全鐵則(對齊 platform-settings):金鑰**只寫不讀** —— 送出後欄位立即清空、頁面絕不回填/
// 顯示全 key,只顯示後端回的遮罩末四碼;**僅 platform-owner**(`canGrantRoles`)且後端 `can_edit`
// 為 true 才顯示設定表單,其餘角色唯讀。設定/清除皆走後端稽核。

import { useCallback, useEffect, useReducer, useState } from 'react';
import { canGrantRoles, useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { deleteAiKey, fetchAiKeys, testAiKey, updateAiKey, type AiKeyEntry } from './aiKeysApi';
import {
  canSubmitKey,
  clearKeyConfirm,
  effectiveGroup,
  groupAiKeys,
  initialKeyFormState,
  initialKeyTestState,
  keyFormReducer,
  keyHintText,
  reservedConsumerDescriptor,
  sourceLabel,
  testResultText,
  testResultTone,
  type KeyTestState,
} from './assistantKeyForm';
import '../_shared/shared.css';
import './assistant.css';

function apiErrorText(e: unknown, fallback: string): { message: string; requestId: string | null } {
  if (e instanceof ApiError) return { message: `${e.message}（${e.code}）`, requestId: e.requestId };
  return { message: fallback, requestId: null };
}

/** 依有效分組給附註說明(admin 指向助理浮窗;dev-portal 標受管保留槽/消費端未接)。 */
function ConsumerNote({ entry }: { entry: AiKeyEntry }) {
  const group = effectiveGroup(entry);
  if (group === 'dev-portal') {
    const who = reservedConsumerDescriptor(entry.consumer);
    return (
      <p className="adm-hint aikey-note">
        <span aria-hidden="true">ⓘ</span>
        <strong>受管金鑰</strong>:供開發者入口的 {who} 消費端使用(已接線);未設金鑰時該功能於 portal 端停用。
      </p>
    );
  }
  if (group === 'admin') {
    return (
      <p className="adm-hint aikey-note">
        <span aria-hidden="true">ⓘ</span>
        供 admin 管理助理(右下角浮窗)使用;助理的啟用/可用狀態即以此金鑰為準。
      </p>
    );
  }
  return null;
}

/**
 * 每卡「測試」控制(僅 editable + key_configured 時渲染)。純呈現:狀態由 `state` 注入,
 * 便於用 renderToStaticMarkup 分別驗證 idle / loading / ok / fail。
 */
export function AiKeyTestRow({ state, onTest }: { state: KeyTestState; onTest: () => void }) {
  const testing = state.status === 'testing';
  const showPill = state.status === 'ok' || state.status === 'fail';
  return (
    <div className="aikey-test-row">
      <button type="button" className="btn-ghost btn-sm" onClick={onTest} disabled={testing}>
        {testing ? '測試中…' : '測試連線'}
      </button>
      {showPill && (
        <span className={`pill ${testResultTone(state)}`} role="status">
          {testResultText(state)}
        </span>
      )}
      <span className="aikey-test-hint text-tertiary">對已設定的金鑰打一發即時探測(不落任何對話記錄)。</span>
    </div>
  );
}

/**
 * 金鑰設定/清除表單(僅 editable 卡「更新金鑰」展開時渲染)。抽成獨立元件 → 收合即整個
 * unmount、卡片高度歸零;重新展開也是全新 initialKeyFormState,剛打的金鑰不會殘留/回填。
 * 表單狀態走 keyFormReducer(送出成功即歸零 → 金鑰絕不回填)。
 */
export function AiKeyEditForm({
  entry,
  onEntryChange,
  onCollapse,
}: {
  entry: AiKeyEntry;
  onEntryChange: (updated: AiKeyEntry) => void;
  onCollapse: () => void;
}) {
  const [form, dispatch] = useReducer(keyFormReducer, initialKeyFormState);
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  async function onSave() {
    const key = form.value.trim();
    if (!key) return;
    dispatch({ type: 'saveStart' });
    try {
      const updated = await updateAiKey(entry.consumer, key);
      onEntryChange(updated);
      dispatch({ type: 'saveOk' }); // ← 欄位立即歸零,絕不回填
    } catch (e) {
      const { message, requestId } = apiErrorText(e, '儲存失敗，請稍後再試。');
      dispatch({ type: 'saveError', message, requestId });
    }
  }

  async function onClear() {
    setClearing(true);
    setClearError(null);
    try {
      const updated = await deleteAiKey(entry.consumer);
      onEntryChange(updated);
      setConfirming(false);
    } catch (e) {
      setClearError(apiErrorText(e, '清除失敗，請稍後再試。').message);
    } finally {
      setClearing(false);
    }
  }

  const confirmCopy = clearKeyConfirm(entry);

  return (
    <form
      className="aikey-form"
      onSubmit={(e) => {
        e.preventDefault();
        void onSave();
      }}
    >
      <div className="aikey-form-head">
        <span className="adm-field-label text-secondary">{entry.key_configured ? '更新金鑰' : '設定金鑰'}</span>
        <button type="button" className="aikey-collapse-btn" onClick={onCollapse} disabled={form.saving}>
          收合
        </button>
      </div>

      {/* input + 顯示/隱藏 + 儲存 + 清除 收成一列(窄視窗自動換行) */}
      <div className="aikey-input-row">
        <input
          type={form.reveal ? 'text' : 'password'}
          value={form.value}
          onChange={(e) => dispatch({ type: 'edit', value: e.target.value })}
          placeholder="貼上 API 金鑰（例如 sk-…）"
          autoComplete="off"
          spellCheck={false}
          disabled={form.saving}
          aria-label={`${entry.label} 的 API 金鑰`}
        />
        <button type="button" className="btn-ghost btn-sm" onClick={() => dispatch({ type: 'toggleReveal' })} disabled={!form.value}>
          {form.reveal ? '隱藏' : '顯示'}
        </button>
        <button type="submit" className="btn-primary btn-sm" disabled={!canSubmitKey(form)}>
          {form.saving ? '儲存中…' : '儲存'}
        </button>
        {entry.source === 'stored' && (
          <button
            type="button"
            className="btn-ghost-danger btn-sm"
            onClick={() => {
              setClearError(null);
              setConfirming(true);
            }}
            disabled={form.saving}
          >
            清除
          </button>
        )}
      </div>

      {form.notice.kind === 'saved' && <p className="aikey-ok">已更新 ✓</p>}
      {form.notice.kind === 'error' && (
        <p className="error-text aikey-err">
          {form.notice.message}
          {form.notice.requestId && <span className="aikey-reqid adm-mono">request_id: {form.notice.requestId}</span>}
        </p>
      )}

      {confirming && (
        <ConfirmDialog
          title={confirmCopy.title}
          tone="danger"
          warning={confirmCopy.warning}
          confirmLabel={confirmCopy.confirmLabel}
          busy={clearing}
          error={clearError}
          onConfirm={() => void onClear()}
          onCancel={() => {
            if (!clearing) {
              setConfirming(false);
              setClearError(null);
            }
          }}
          body={
            <div>
              <p>{confirmCopy.body}</p>
              <dl className="adm-kv">
                <dt>消費端</dt>
                <dd className="adm-mono">{entry.consumer}</dd>
                <dt>名稱</dt>
                <dd>{entry.label}</dd>
              </dl>
            </div>
          }
        />
      )}
    </form>
  );
}

/**
 * 單一 consumer 卡(緊湊版):
 *  ①頭列 — 名稱 + consumer id(同列、muted)在左,啟用/停用 pill 在右。
 *  ②狀態條 — 金鑰(pill + 遮罩末四碼)與來源濃縮成一行(啟用狀態已在頭列 pill,不再重複列出)。
 *  ③附註 → ④測試連線列(editable + 已設定)→ ⑤「更新金鑰」收合式表單。
 * 狀態投影所有 CanAdminRead 角色可見;設定/清除表單僅 editable(platform-owner ∧ 後端 can_edit),
 * 且**預設收合** → 未展開時卡片很矮,一屏可覽全部 consumer 的狀態。
 */
export function AiKeyConsumerCard({
  entry,
  editable,
  onEntryChange,
}: {
  entry: AiKeyEntry;
  /** 是否顯示設定表單(= platform-owner 且後端 can_edit)。 */
  editable: boolean;
  onEntryChange: (updated: AiKeyEntry) => void;
}) {
  const [testState, setTestState] = useState<KeyTestState>(initialKeyTestState);
  const [editing, setEditing] = useState(false);

  const hint = keyHintText(entry);

  async function onTest() {
    setTestState({ status: 'testing' });
    try {
      const result = await testAiKey(entry.consumer);
      // 200 但 ok=false(可達但金鑰無效)也走 ok 分支,由 result.ok 決定 pill 色。
      setTestState({ status: 'ok', result });
    } catch (e) {
      const { message, requestId } = apiErrorText(e, '測試失敗,請稍後再試。');
      setTestState({ status: 'fail', message, requestId });
    }
  }

  return (
    <section className="card aikey-card">
      <div className="aikey-card-head">
        <div className="aikey-card-title">
          <h2 className="adm-card-title">{entry.label}</h2>
          <span className="aikey-consumer adm-mono">{entry.consumer}</span>
        </div>
        <span className={`pill pill-dot ${entry.enabled ? 'pill-good' : 'pill-warn'}`}>{entry.enabled ? '啟用中' : '停用'}</span>
      </div>

      {/* 狀態濃縮成一條 inline 行:金鑰 + 遮罩末四碼 + 來源 */}
      <div className="aikey-status">
        <span className="aikey-stat">
          <span className="aikey-stat-label">金鑰</span>
          <span className={`pill pill-dot ${entry.key_configured ? 'pill-good' : 'pill-danger'}`}>{entry.key_configured ? '已設定' : '未設定'}</span>
          {hint && <code className="aikey-hint adm-mono">{hint}</code>}
        </span>
        <span className="aikey-stat">
          <span className="aikey-stat-label">來源</span>
          <span className="aikey-stat-val adm-mono">{sourceLabel(entry.source)}</span>
        </span>
      </div>

      <ConsumerNote entry={entry} />

      {/* 測試連線:僅 platform-owner(editable)且已設定金鑰時提供 */}
      {editable && entry.key_configured && <AiKeyTestRow state={testState} onTest={() => void onTest()} />}

      {editable ? (
        editing ? (
          <AiKeyEditForm entry={entry} onEntryChange={onEntryChange} onCollapse={() => setEditing(false)} />
        ) : (
          <button type="button" className="btn-ghost btn-sm aikey-edit-toggle" onClick={() => setEditing(true)}>
            {entry.key_configured ? '＋ 更新金鑰' : '＋ 設定金鑰'}
          </button>
        )
      ) : (
        <p className="adm-hint aikey-note">
          <span aria-hidden="true">ⓘ</span>
          僅平台擁有者可設定此金鑰。你目前為唯讀檢視。
        </p>
      )}
    </section>
  );
}

/**
 * 登錄台呈現(接受已載入的 entries + platform-owner 旗標 → 便於用 renderToStaticMarkup 單測)。
 * 每列的可編輯性 = `isPlatformOwner && entry.can_edit`(client role gate ∧ 後端權威)。
 */
export function AiKeyRegistryView({
  entries,
  isPlatformOwner,
  onEntryChange,
}: {
  entries: AiKeyEntry[];
  isPlatformOwner: boolean;
  onEntryChange?: (updated: AiKeyEntry) => void;
}) {
  return (
    <div className="aikey-registry">
      <div className="page-header">
        <div>
          <h1 className="page-title">AI 金鑰登錄台</h1>
          <p className="page-subtitle">集中管理各消費端(consumer)的 LLM API 金鑰。金鑰只寫不讀:此頁只顯示遮罩末四碼與來源,絕不顯示或回填全金鑰。</p>
        </div>
      </div>

      <div className="adm-secret-box adm-secret-box--info aikey-security">
        <p className="adm-secret-warn">🔒 金鑰安全</p>
        <p className="aikey-security-body">
          金鑰以加密方式存放於後端,絕不回傳瀏覽器;此頁只顯示遮罩末四碼。僅平台擁有者可設定,所有設定與清除動作皆會寫入稽核記錄。
        </p>
      </div>

      {groupAiKeys(entries).map((group) => (
        <section className="aikey-group" key={group.key}>
          <h2 className="aikey-group-title">{group.label}</h2>
          <div className="aikey-list">
            {group.entries.map((entry) => (
              <AiKeyConsumerCard
                key={entry.consumer}
                entry={entry}
                editable={isPlatformOwner && entry.can_edit}
                onEntryChange={(updated) => onEntryChange?.(updated)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function AssistantSettingsPage() {
  const { role } = useAuth();
  const isPlatformOwner = canGrantRoles(role);
  const [entries, setEntries] = useState<AiKeyEntry[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    fetchAiKeys(signal)
      .then((ks) => {
        if (!signal?.aborted) setEntries(ks);
      })
      .catch((err) => {
        if (!signal?.aborted) setError(err);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const onEntryChange = useCallback((updated: AiKeyEntry) => {
    setEntries((prev) => (prev ? prev.map((e) => (e.consumer === updated.consumer ? updated : e)) : prev));
  }, []);

  if (loading && !entries) return <LoadingState label="載入 AI 金鑰登錄台…" />;
  if (error || !entries) return <ErrorState error={error} onRetry={() => load()} />;
  return <AiKeyRegistryView entries={entries} isPlatformOwner={isPlatformOwner} onEntryChange={onEntryChange} />;
}
